'use es6';

import { Map as ImmutableMap } from 'immutable';
import { currency, duration, number, percent } from 'reporting-data/hydrate/numberFormatter';
import { formatDate as date } from 'reporting-data/hydrate/propertyFormatter';
export const FormatTypes = {
  NONE: 'none',
  AUTO: 'auto',
  PERCENTAGE: 'percentage',
  NUMBER: 'number',
  CURRENCY: 'currency'
};
export const NegativeNumberTypes = {
  AUTO: 'auto',
  PARENTHESES: 'parentheses',
  LEFT: 'left',
  RIGHT: 'right'
};
export const NumberFormats = {
  currency: 'currency',
  duration: 'duration',
  number: 'number',
  percent: 'percent'
};
export const DateFormats = {
  date: 'date',
  datetime: 'datetime',
  timestamp: 'timestamp'
};
export const formatCurrency = (value, options) => currency(value, options);
export const formatDuration = (value, options) => duration(value, options);
export const formatNumber = (value, options) => {
  const {
    shouldFormat
  } = options;
  return shouldFormat ? number(value, options) : value;
};
export const formatPercent = (value, options) => percent(value * 100, options);
export const formatDate = (value, {
  format = 'l',
  method = 'utc'
}) => date(value, format, method);
export const formatDatetime = (value, {
  format = 'l',
  method
}) => date(value, format, method);
export const formatTimestamp = (value, {
  format = 'lll',
  method = 'userTz'
}) => date(value, format, method);
export const isFormattableProperty = property => property && (property.get('type') === NumberFormats.number || property.get('type') === NumberFormats.currency);
export const applyCustomFormatting = (value, options, companyCurrencyCode) => {
  const delimiter = options.get('useThousandsSeparator') ? undefined : '';
  switch (options.get('type')) {
    case FormatTypes.PERCENTAGE:
      if (value < 0 && options.get('negativeSymbol') === NegativeNumberTypes.PARENTHESES) {
        return `(${percent(value * -100, {
          customPrecision: options.get('customPrecision'),
          delimiter,
          keepInsignificantZeros: true
        })})`;
      }
      if (value < 0 && options.get('negativeSymbol') === NegativeNumberTypes.RIGHT) {
        return `${percent(value * -100, {
          customPrecision: options.get('customPrecision'),
          delimiter,
          keepInsignificantZeros: true
        })}-`;
      }
      return percent(value * 100, {
        customPrecision: options.get('customPrecision'),
        delimiter
      });
    case FormatTypes.CURRENCY:
      if (value < 0 && options.get('negativeSymbol') === NegativeNumberTypes.PARENTHESES) {
        return currency(value * -1, {
          currencySign: 'accounting',
          customPrecision: options.get('customPrecision'),
          currencyCode: options.get('currencyCode') || companyCurrencyCode,
          delimiter
        });
      }
      if (value < 0 && options.get('negativeSymbol') === NegativeNumberTypes.RIGHT) {
        return `${currency(value * -1, {
          customPrecision: options.get('customPrecision'),
          currencyCode: options.get('currencyCode') || companyCurrencyCode,
          delimiter
        })}-`;
      }
      return currency(value, {
        customPrecision: options.get('customPrecision'),
        currencyCode: options.get('currencyCode') || companyCurrencyCode,
        delimiter
      });
    case FormatTypes.NUMBER:
      if (value < 0 && options.get('negativeSymbol') === NegativeNumberTypes.PARENTHESES) {
        return `(${number(value * -1, {
          customPrecision: options.get('customPrecision'),
          delimiter,
          keepInsignificantZeros: true
        })})`;
      }
      if (value < 0 && options.get('negativeSymbol') === NegativeNumberTypes.RIGHT) {
        return `${number(value * -1, {
          customPrecision: options.get('customPrecision'),
          delimiter,
          keepInsignificantZeros: true
        })}-`;
      }
      return number(value, {
        customPrecision: options.get('customPrecision'),
        delimiter,
        keepInsignificantZeros: true
      });
    default:
      return number(value, options);
  }
};
const NumberFormatFunctions = {
  [NumberFormats.currency]: formatCurrency,
  [NumberFormats.duration]: formatDuration,
  [NumberFormats.number]: formatNumber,
  [NumberFormats.percent]: formatPercent
};
const DateFormatFunctions = {
  [DateFormats.date]: formatDate,
  [DateFormats.datetime]: formatDatetime,
  [DateFormats.timestamp]: formatTimestamp
};
export const getPropertyFormatter = (property = ImmutableMap(), overrideOptions) => {
  const formattingObject = property.get('formattingObject');
  const propertyType = property.get('type');
  const propertyMeta = property.get('propertyMeta');
  const isDistinctCountAggregation = propertyMeta && propertyMeta.get('aggregationType') === 'DISTINCT_APPROX';
  const options = {};
  if (!isDistinctCountAggregation && (propertyType === NumberFormats.number || propertyType === NumberFormats.currency) && formattingObject && formattingObject.type !== 'auto') {
    const companyCurrencyCode = property.get('companyCurrencyCode');
    return value => applyCustomFormatting(value, formattingObject, companyCurrencyCode);
  }
  if (!isDistinctCountAggregation && formattingObject && formattingObject.type === 'auto' && property.get('numberDisplayHint') === 'percentage') {
    return value => formatPercent(value, Object.assign({}, options, overrideOptions));
  }
  if (formattingObject && formattingObject.type === 'auto' && property.get('numberDisplayHint') === 'unformatted') {
    return value => value && value.toString();
  }
  if (propertyType === NumberFormats.duration) {
    options.durationUnit = property.getIn(['format', 'durationUnit']);
    options.smallScale = property.get('shouldBeSmallScale');
    options.minimumDisplayUnit = property.getIn(['format', 'minimumDisplayUnit']);
  }
  if (propertyType === NumberFormats.currency && !isDistinctCountAggregation) {
    options.currencyCode = property.getIn(['format', 'currencyCode']) || property.get('companyCurrencyCode');
  }
  if (propertyType === NumberFormats.number) {
    options.shouldFormat = property.get('numberDisplayHint') !== 'unformatted';
  }
  if (propertyMeta && propertyMeta.has('customPrecision')) {
    options.customPrecision = propertyMeta.get('customPrecision');
  }
  const isGranularDateTime = propertyType === DateFormats.datetime && property.getIn(['format', 'frequency']) === 'MINUTE';
  if (isGranularDateTime) {
    return value => formatTimestamp(value, 'lll', 'userTz');
  }
  const formatFn = NumberFormatFunctions[propertyType] || DateFormatFunctions[propertyType] || (value => value);
  return value => formatFn(value, Object.assign({}, options, overrideOptions));
};
export const formatDecimalColumnToPercent = (rows, field) => {
  return rows.map(row => {
    if (row.has(field)) {
      const value = row.get(field);
      return row.set(field, formatPercent(value));
    }
    return row;
  });
};